import React, { useState, useLayoutEffect } from 'react';
import * as Yup from 'yup';
import { Link as LinkIcon, Question } from '@menuzen/icon/build/outline';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Box, InputAdornment, useTheme } from '@mui/material';
import Dialog, { DialogProps } from '../../common/components/dialog/Dialog';

import { useNode, BuilderComponentProps } from '@mnz/builder';
import { capitalizeFirstLetter } from '../../shared/utils/string';
import { linksWithoutGuide, OrderOptionData, orderOptions } from '../../constant/orderLinks';
import FormContainer from '../../common/components/form/FormContainer';
import TextFieldElement from '../../common/components/form/TextFormField';
import SelectFormField from '../../common/components/form/SelectFormField';
import LinkGuideModal from '../LinkGuideModal';
import { labelCss } from '../../common/style';
import { px2rem } from '../../core';

interface EditOrderLinkProps extends DialogProps {
    selectedOrderLink: OrderLinkValues | null;
    setSelectedOrderLink: (data: OrderLinkValues | null) => void;
    onClose: () => void;
}

export interface OrderLinkValues extends Link {
    id: number;
}

export interface Link {
    link: string;
    label?: string;
    type: string;
}

const renderLabelURL = (company: string) => {
    const orderOption: OrderOptionData | undefined = orderOptions.find((item) => {
        return item.value == company;
    });
    if (orderOption && orderOption.title) {
        return `Enter ${orderOption.title} URL`;
    }
    return `Enter ${capitalizeFirstLetter(company)} URL`;
};

const renderPrefixURL = () => {
    return (
        <Box sx={{ fontSize: '15px' }}>
            <LinkIcon />
        </Box>
    );
};

const renderPlaceHolderURL = (company: string) => {
    const orderOption: OrderOptionData | undefined = orderOptions.find((item) => {
        return item.value == company;
    });
    if (orderOption && orderOption.title) {
        return `${orderOption.title} URL`;
    }
    return `${capitalizeFirstLetter(company)} URL`;
};

const orderLinkSchema = Yup.object().shape({
    type: Yup.string(),
    link: Yup.string().required('URL is required.'),
    label: Yup.string().max(50, 'Text may not be greater than 50 characters.'),
});

const EditOrderLinkModal: React.FC<EditOrderLinkProps> = ({
    setSelectedOrderLink,
    selectedOrderLink,
    onClose,
    ...props
}) => {
    const theme = useTheme();
    const {
        actions: { setAccount },
        props: { account },
    } = useNode((node) => ({
        props: node.data.props as unknown as BuilderComponentProps,
    }));

    const orderLinks = account.orderLinks;
    const [openModalLinkGuide, setOpenModalLinkGuide] = useState<boolean>(false);

    const onSubmit = (data: OrderLinkValues) => {
        const values: Link = {
            type: data.type,
            label: data.label,
            link: data.link,
        };
        if (selectedOrderLink) {
            const selectedIndex = orderLinks.findIndex((social) => social.id == selectedOrderLink.id);

            const newData = [...orderLinks];
            newData[selectedIndex] = { ...newData[selectedIndex], ...values };
            setAccount({ orderLinks: newData });
        } else {
            let newData: Link[] = [];
            newData = [...orderLinks, values];
            setAccount({ orderLinks: newData });
        }
        setSelectedOrderLink(null);
        onClose();
    };

    const deleteLink = () => {
        if (selectedOrderLink && orderLinks) {
            const selectedIndex = orderLinks.findIndex((social) => social.id == selectedOrderLink.id);

            const newData = [...orderLinks];
            newData.splice(selectedIndex, 1);
            setAccount({ orderLinks: newData });
            setSelectedOrderLink(null);
            onClose();
        }
    };

    const defaultValues = {
        id: selectedOrderLink ? selectedOrderLink.id : -1,
        type: selectedOrderLink ? selectedOrderLink.type : 'CUSTOM',
        link: selectedOrderLink ? selectedOrderLink.link : '',
        label: selectedOrderLink ? selectedOrderLink.label : '',
    };

    const methods = useForm<OrderLinkValues>({
        mode: 'onChange',
        reValidateMode: 'onBlur',
        resolver: yupResolver(orderLinkSchema),
        defaultValues,
    });

    useLayoutEffect(() => {
        methods.reset(defaultValues);
    }, [props.open]);

    const { type } = methods.watch();

    return (
        <Dialog
            onClose={onClose}
            anchor="bottom"
            title={selectedOrderLink ? 'Edit Ordering Link' : 'Add Ordering Link'}
            showClose={true}
            {...props}
        >
            <LinkGuideModal
                type={type.toLowerCase()}
                showClose={true}
                anchor="bottom"
                open={openModalLinkGuide}
                onClose={() => setOpenModalLinkGuide(false)}
            />
            <FormContainer formContext={methods} onSubmit={onSubmit}>
                <Box sx={{ mt: '20px', px: '30px', pb: '40px' }} display="flex" flexDirection="column">
                    <Box mb={2.5}>
                        <Box mb={1} sx={{ ...labelCss() }}>
                            Select Ordering Platform
                        </Box>
                        <SelectFormField id="type" name="type" options={orderOptions} sx={{ width: '100%' }} />
                    </Box>

                    <Box mb={1}>
                        <Box mb={1} sx={{ ...labelCss() }}>
                            {renderLabelURL(type)}
                        </Box>
                        <TextFieldElement
                            id="link"
                            name="link"
                            fullWidth
                            placeholder={renderPlaceHolderURL(type)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ fontSize: 18 }}>
                                        {renderPrefixURL()}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {!linksWithoutGuide.includes(type) && (
                        <Box
                            sx={{
                                marginTop: px2rem(10),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'start',
                                color: theme.palette.success.main,
                            }}
                        >
                            <Box sx={{ fontSize: px2rem(12), marginRight: px2rem(2) }}>
                                <Question />
                            </Box>
                            <p
                                onClick={() => {
                                    if (type) {
                                        setOpenModalLinkGuide(true);
                                    }
                                }}
                                css={{
                                    fontSize: px2rem(10),
                                    fontWeight: 'bold',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                }}
                            >
                                How do I find my URL?
                            </p>
                        </Box>
                    )}
                    <Box mt="15px">
                        <Box mb={1}>Custom Button Text</Box>
                        <TextFieldElement id="label" name="label" placeholder="Order Online" fullWidth />
                    </Box>
                    <Box display="flex" justifyContent="center" sx={{ mt: '30px', mx: '-5px' }}>
                        <Box sx={{ px: '5px', flex: 1 }}>
                            {selectedOrderLink ? (
                                <Button color={`error`} variant={`outlined`} fullWidth={true} onClick={deleteLink}>
                                    Delete
                                </Button>
                            ) : (
                                <Button variant={`outlined`} fullWidth={true} onClick={onClose}>
                                    Cancel
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ px: '5px', flex: 1 }}>
                            <Button color={`primary`} fullWidth={true} type="submit">
                                {selectedOrderLink ? 'Edit link' : 'Done'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </FormContainer>
        </Dialog>
    );
};

export default EditOrderLinkModal;
